<template>
    <form class="rf-view promotion-view" @submit.prevent="next(false)">
        <NavigationBar :title="promotion.settings.title.get($i18n)">
            <BackButton v-if="canPop" slot="left" @click="pop" />
            <button v-else-if="canDismiss" slot="right" class="button icon close" type="button" @click="(e) => dismiss()" />
        </NavigationBar>
        <main @click="blur">
            <h1 v-text="promotion.settings.title.get($i18n)" />
            <h3 v-text="promotion.settings.subtitle.get($i18n)" />

            <PromotionBox :promotion="promotion" :header="false" :language="customer.settings.language" />

            <p v-if="alreadyOrderedCount > 0" class="info-box">
                {{ $tc('promotion.alreadyOrderedText', alreadyOrderedCount) }}
            </p>

            <p v-if="disableOrder" class="error-box">
                {{ $tc('promotion.maximumText', 0) }}
            </p>

            <p class="description" v-text="promotion.settings.description.get($i18n)" />

            <p class="style-description valid-date" v-if="!promotion.settings.canBeCombined">
                {{ $t('promotion.canNotBeCombined') }}
            </p>

            <p class="style-description valid-date">
                {{ $t('promotion.validUntil', { date: formatDate(promotion.endAt) } ) }}
            </p>

            <p v-if="totalAmount > 0" class="style-description valid-date">
                {{ $t('promotion.numberOfItems', { items: promotion.settings.totalAmount }) }}
            </p>
        </main>

        <div v-if="showDeliveryAddressPopup" class="popup-overlay">
            <div class="popup-content">
                <h3>{{ $t('promotion.deliveryAddressRequired') }}</h3>
                <p>{{ $t('promotion.deliveryAddressMessage') }}</p>
                <button @click="closePopup" class="button primary">{{ $t('general.ok') }}</button>
            </div>
        </div>

        <FloatingFooter v-if="promotion.settings.enableOrders && !disableOrder" class="noSticky">
            <InputBox v-if="!disableOrder" :title="$t('fields.amount')" :error-box="errorBox" error-fields="amount">
                <NumberInput v-model="amount" :suffix-singular="$t('fields.amountSuffixSingular')"
                    :suffix="$t('fields.amountSuffix')" :min="minimum" :max="remainingMaximum" :stepper="true"
                    :validator="validator" />
            </InputBox>

            <p v-if="promotion.settings.maximumAmount !== null" class="style-description">
                {{ $tc('promotion.maximumText', promotion.settings.maximumAmount) }}
            </p>

            <button v-if="promotion.settings.button" class="button secundary" type="button"
                @click="openURL(promotion.settings.button.url.get($i18n))">
                {{ promotion.settings.button.text.get($i18n) }}
            </button>
            <LoadingButton :loading="loading" class="block">
                <button class="button primary" @click="next(false)">
                    {{ $t('promotions.orderButton') }}
                </button>

                <button class="button primary" v-if="this.customerNumber > 1" @click="next(true)">
                    {{ $tc('promotions.orderForAllMyClientsButton', this.customerNumber) }}
                </button>
            </LoadingButton>
        </FloatingFooter>

        <FloatingFooter v-else-if="promotion.settings.button" class="noSticky">
            <LoadingButton :loading="loading" class="block">
                <button v-if="promotion.settings.button" class="button secundary" type="button"
                    @click="openURL(promotion.settings.button.url.get($i18n))">
                    {{ promotion.settings.button.text.get($i18n) }}
                </button>
            </LoadingButton>
        </FloatingFooter>
    </form>
</template>


<script lang="ts">
import { BackButton,FloatingFooter, InputBox, List, ListItem,LoadingButton, NavigationBar, NumberInput,PromotionBox } from "@restofrit/components"
import { Session } from "@restofrit/networking";
import { Customer, CustomerStatus, Promotion, PromotionCustomer } from "@restofrit/structures";
import { Formatter } from "@restofrit/utilities";
import { ArrayDecoder, Decoder } from "@simonbackx/simple-encoding";
import { ErrorBox, GeneralErrorsView, Validator } from "@simonbackx/simple-error-forms";
import { ComponentWithProperties, NavigationMixin } from "@simonbackx/vue-app-navigation";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { Browser } from '@capacitor/browser';

import ConfirmPromotionView from "./ConfirmPromotionView.vue";

@Component({
    components: {
        InputBox,
        GeneralErrorsView,
        NumberInput,
        LoadingButton,
        FloatingFooter,
        NavigationBar,
        List, 
        ListItem,
        PromotionBox,
        BackButton
    },
})
export default class PromotionView extends Mixins(NavigationMixin) {
    loading = false

    @Prop({ required: true })
    promotion!: PromotionCustomer

    @Prop({ required: true })
    customer: Customer

    customerNumber: number = 1;

    validator = new Validator()
    errorBox: ErrorBox | null = null
    amount = this.minimum

    showDeliveryAddressPopup = false

    mounted() {
        // Get customers for user
        Session.shared.authenticatedServer.request({
            method: "GET",
            path: "/customers/customers",
            decoder: new ArrayDecoder(Customer as Decoder<Customer>)
        }).then(response => {
            const customersForWholesaler = response.data.filter(c => 
                c.wholesaler.id == this.customer.wholesaler.id && 
                c.settings.status == CustomerStatus.Accepted &&
                c.settings.categories.some(category => this.promotion.settings.categories.includes(category))
            )

            this.customerNumber = customersForWholesaler ? customersForWholesaler.length : 1;
        })

        // Register view
        Session.shared.authenticatedServer.request({
            method: "POST",
            path: "/customers/"+this.customer.id+"/promotions/"+this.promotion.id+"/viewed",
        }).catch(console.error)

        if (this.promotion.settings.requireDeliveryAddress && 
        !(this.customer.settings.city && this.customer.settings.postalCode && this.customer.settings.address)) {
            this.showDeliveryAddressPopup = true
        }
    }

    formatDate(d: Date) {
        return Formatter.date(d)
    }

    async openURL(url: string) {
        await Browser.open({ url: url });
    }
   
    next(orderToAllMyClients: boolean) {
        this.show(new ComponentWithProperties(ConfirmPromotionView, {
                promotion: this.promotion,
                customer: this.customer,
                amount: this.amount,
                orderToAllMyClients: orderToAllMyClients,
                customerNumber: this.customerNumber
            }))
    }

    get alreadyOrderedCount() {
        return this.promotion.orders.reduce((count, order) => count + order.settings.amount, 0)
    }

    get totalAmount() {
        return this.promotion.settings.totalAmount || 0
    }

    get remainingMaximum() {
        if (this.promotion.settings.maximumAmount === null) {
            return null
        }
        return this.promotion.settings.maximumAmount - this.alreadyOrderedCount
    }

    get minimum() {
        return Math.max(1, (this.promotion.settings.minimumAmount || 1) - this.alreadyOrderedCount)
    }

    get disableOrder() {
        if (this.promotion.settings.maximumAmount === null) {
            return false
        }
        return this.promotion.settings.maximumAmount - this.alreadyOrderedCount < 1
    }

    blur() {
        (document.activeElement as any)?.blur()
    }

    closePopup() {
        this.showDeliveryAddressPopup = false
    }
}
</script>

<style lang="scss">
@use '~@restofrit/scss/base/variables.scss' as *;
@use '~@restofrit/scss/base/text-styles.scss' as *;

.promotion-view {
    > main > h1 {
        padding-bottom: 5px !important;
        white-space: pre-wrap;
    }
    > main > h3 {
        @extend .style-h4;
        color: $color-primary;
        padding-bottom: 20px;
        white-space: pre-wrap;
    }
    // todo

    .description {
        @extend .style-description;
        padding-top: 20px;
        white-space: pre-wrap;
    }

    .valid-date {
        padding-top: 10px;
        font-style: italic;
    }

    .button {
        margin-bottom: 10px;
    }

    .popup-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
    }

    .popup-content {
        background-color: white;
        padding: 20px;
        border-radius: 8px;
        text-align: center;

        h3 {
            margin-bottom: 10px;
        }

        p {
            margin-bottom: 20px;
        }
    }
}
</style>